import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BugsnagService } from '@mode/shared/util-error-handling';
import { INITIATOR_CONTEXT } from '@mode/shared/util-halogen';
import { Key, pathToRegexp } from 'path-to-regexp';
import { parse } from 'query-string';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Md5 } from 'ts-md5';

/**
 * List of all routes in Rails served by the ApiController.
 * Command to generate:
 * bundle exec rails routes -g "(\/api\/)|(\/internal\/)" | grep -oE '(\/.*?)(\(.*?\))' | sed 's/\(.*\)/'\''\1'\'',/g' |  uniq
 * Delete the /api/*unknown route before saving
 */
const RAILS_ROUTES = [
  '/internal/:account_id/reports/:id/import_dataset(.:format)',
  '/internal/:account_id/reports/:id(.:format)',
  '/api/docs(.:format)',
  '/api/slack_events(.:format)',
  '/api/uploads(.:format)',
  '/api/uploads/:id(.:format)',
  '/api/stars(.:format)',
  '/api/stars/:id(.:format)',
  '/api/color_palettes/predefined(.:format)',
  '/api/table/validate(.:format)',
  '/api/data_sources(.:format)',
  '/api/data_source_bridges/update_batch(.:format)',
  '/api/:account_id(.:format)',
  '/api/:account_id/explorations/user/:user_id(.:format)',
  '/api/:account_id/explorations(.:format)',
  '/api/:account_id/explorations/:id/edit(.:format)',
  '/api/:account_id/explorations/:id(.:format)',
  '/api/:account_id/ai/chat(.:format)',
  '/api/:account_id/visualizations/:id(.:format)',
  '/api/:account_id/saml_identity_providers(.:format)',
  '/api/:account_id/saml_identity_providers/:id(.:format)',
  '/api/:account_id/easy_identity_providers(.:format)',
  '/api/:account_id/memberships/lite(.:format)',
  '/api/:account_id/memberships/search(.:format)',
  '/api/:account_id/memberships/:state(.:format)',
  '/api/:account_id/memberships(.:format)',
  '/api/:account_id/memberships/:id(.:format)',
  '/api/:account_id/scim_tokens(.:format)',
  '/api/:account_id/scim_tokens/:id(.:format)',
  '/api/:account_id/organizations(.:format)',
  '/api/:account_id/integrations/dbt/:id(.:format)',
  '/api/:account_id/admins(.:format)',
  '/api/:account_id/report_themes(.:format)',
  '/api/:account_id/report_themes/:id(.:format)',
  '/api/:account_id/schedules(.:format)',
  '/api/:account_id/color_palettes(.:format)',
  '/api/:account_id/color_palettes/:id(.:format)',
  '/api/:account_id/trial_appointments(.:format)',
  '/api/:account_id/trial_appointments/:id(.:format)',
  '/api/:account_id/slack_app/channels(.:format)',
  '/api/:account_id/slack_app/channels/:id(.:format)',
  '/api/:account_id/slack_app(.:format)',
  '/api/:account_id/github_apps/:github_app_id/unsynced_definitions(.:format)',
  '/api/:account_id/github_apps/:id(.:format)',
  '/api/:account_id/preferences(.:format)',
  '/api/:account_id/tables/:table_id/imports/:id(.:format)',
  '/api/:account_id/tables/:id/preview(.:format)',
  '/api/:account_id/tables/:id(.:format)',
  '/api/:account_id/tables/:table_id/columns(.:format)',
  '/api/:account_id/tables/:table_id/columns/new(.:format)',
  '/api/:account_id/tables/:table_id/columns/:id/edit(.:format)',
  '/api/:account_id/tables/:table_id/columns/:id(.:format)',
  '/api/:account_id/tables(.:format)',
  '/api/:account_id/tables/:id(.:format)',
  '/api/:account_id/github_syncs/:id(.:format)',
  '/api/:account_id/spaces/validate(.:format)',
  '/api/:account_id/spaces/:id/detail(.:format)',
  '/api/:account_id/spaces/:id/viewed(.:format)',
  '/api/:account_id/spaces/:space_id/memberships/preview(.:format)',
  '/api/:account_id/spaces/:space_id/memberships(.:format)',
  '/api/:account_id/spaces/:space_id/memberships/:id(.:format)',
  '/api/:account_id/spaces/:space_id/permissions/search(.:format)',
  '/api/:account_id/spaces/:space_id/permissions(.:format)',
  '/api/:account_id/spaces/:space_id/permissions/:id(.:format)',
  '/api/:account_id/spaces/:space_id/reports(.:format)',
  '/api/:account_id/spaces/:space_id/datasets(.:format)',
  '/api/:account_id/spaces/:space_id/base_reports(.:format)',
  '/api/:account_id/spaces/:space_id/report_pins(.:format)',
  '/api/:account_id/spaces/:space_id/report_pins/:id(.:format)',
  '/api/:account_id/spaces(.:format)',
  '/api/:account_id/spaces/:id(.:format)',
  '/api/:account_id/collections/validate(.:format)',
  '/api/:account_id/collections/:id/detail(.:format)',
  '/api/:account_id/collections/:id/viewed(.:format)',
  '/api/:account_id/collections/:collection_id/memberships/preview(.:format)',
  '/api/:account_id/collections/:collection_id/memberships(.:format)',
  '/api/:account_id/collections/:collection_id/memberships/:id(.:format)',
  '/api/:account_id/collections/:collection_id/permissions/search(.:format)',
  '/api/:account_id/collections/:collection_id/permissions(.:format)',
  '/api/:account_id/collections/:collection_id/permissions/:id(.:format)',
  '/api/:account_id/collections/:collection_id/reports(.:format)',
  '/api/:account_id/collections/:collection_id/datasets(.:format)',
  '/api/:account_id/collections/:collection_id/base_reports(.:format)',
  '/api/:account_id/collections/:collection_id/report_pins(.:format)',
  '/api/:account_id/collections/:collection_id/report_pins/:id(.:format)',
  '/api/:account_id/collections(.:format)',
  '/api/:account_id/collections/:id(.:format)',
  '/api/:account_id/data_source_connection_request(.:format)',
  '/api/:account_id/data_sources/:id/purge(.:format)',
  '/api/:account_id/data_sources/:data_source_id/nodes(.:format)',
  '/api/:account_id/data_sources/:data_source_id/nodes/:id(.:format)',
  '/api/:account_id/data_sources/:data_source_id/schema_updates(.:format)',
  '/api/:account_id/data_sources/:data_source_id/permissions(.:format)',
  '/api/:account_id/data_sources/:data_source_id/permissions/:id(.:format)',
  '/api/:account_id/data_sources/:data_source_id/reports(.:format)',
  '/api/:account_id/data_sources/:data_source_id/datasets(.:format)',
  '/api/:account_id/data_sources/:data_source_id/base_reports(.:format)',
  '/api/:account_id/data_sources(.:format)',
  '/api/:account_id/data_sources/:id(.:format)',
  '/api/:account_id/data_source_schema_updates/:id/fail(.:format)',
  '/api/:account_id/data_source_schema_updates/:id(.:format)',
  '/api/:account_id/dbt_metrics(.:format)',
  '/api/:account_id/dbt_metrics/:id(.:format)',
  '/api/:account_id/definitions/validate(.:format)',
  '/api/:account_id/definitions/:id/execute(.:format)',
  '/api/:account_id/definitions/:id/sync_to_github(.:format)',
  '/api/:account_id/definitions(.:format)',
  '/api/:account_id/definitions/new(.:format)',
  '/api/:account_id/definitions/:id/edit(.:format)',
  '/api/:account_id/definitions/:id(.:format)',
  '/api/:account_id/groups/:group_id/memberships(.:format)',
  '/api/:account_id/groups/:group_id/memberships/:id(.:format)',
  '/api/:account_id/groups(.:format)',
  '/api/:account_id/groups/:id(.:format)',
  '/api/:account_id/datasets/:dataset_id/runs(.:format)',
  '/api/:account_id/datasets/:dataset_id/schedules(.:format)',
  '/api/:account_id/datasets/:id/reports(.:format)',
  '/api/:account_id/datasets/:id/sync_to_github(.:format)',
  '/api/:account_id/datasets(.:format)',
  '/api/:account_id/datasets/:id(.:format)',
  '/api/:account_id/datasets/:report_id/runs/:id/clone(.:format)',
  '/api/:account_id/datasets/:report_id/runs/:id(.:format)',
  '/api/:account_id/datasets/:report_id/runs(.:format)',
  '/api/:account_id/datasets/:id(.:format)',
  '/api/:account_id/base_reports(.:format)',
  '/api/:account_id/switch_views/:switch_view_id/switch_attrs(.:format)',
  '/api/:account_id/switch_views/:switch_view_id/switch_attrs/new(.:format)',
  '/api/:account_id/switch_views/:switch_view_id/switch_attrs/:id(.:format)',
  '/api/:account_id/switch_views/:switch_view_id/visualizations(.:format)',
  '/api/:account_id/switch_views/:switch_view_id/visualizations/:id(.:format)',
  '/api/:account_id/switch_views/:id(.:format)',
  '/api/:account_id/reports/:report_id/results/content(.:format)',
  '/api/:account_id/reports/:report_id/rotate_share_keys(/:run_id)',
  '/api/:account_id/reports/purge(.:format)',
  '/api/:account_id/reports/:report_id/visualizations(.:format)',
  '/api/:account_id/reports/:report_id/dbt_metadata(.:format)',
  '/api/:account_id/reports/:report_id/queries/:query_id/charts(.:format)',
  '/api/:account_id/reports/:report_id/queries/:query_id/charts/new(.:format)',
  '/api/:account_id/reports/:report_id/queries/:query_id/charts/:id(.:format)',
  '/api/:account_id/reports/:report_id/queries/:query_id/tables(.:format)',
  '/api/:account_id/reports/:report_id/queries/:query_id/tables/new(.:format)',
  '/api/:account_id/reports/:report_id/queries/:query_id/tables/:id(.:format)',
  '/api/:account_id/reports/:report_id/queries/:query_id/runs(.:format)',
  '/api/:account_id/reports/:report_id/queries(.:format)',
  '/api/:account_id/reports/:report_id/queries/new(.:format)',
  '/api/:account_id/reports/:report_id/queries/:id(.:format)',
  '/api/:account_id/reports/:report_id/filters/update_batch(.:format)',
  '/api/:account_id/reports/:report_id/filters(.:format)',
  '/api/:account_id/reports/:report_id/filters/:id(.:format)',
  '/api/:account_id/reports/:report_id/pins(.:format)',
  '/api/:account_id/reports/:report_id/pins/new(.:format)',
  '/api/:account_id/reports/:report_id/pins/:id(.:format)',
  '/api/:account_id/reports/:report_id/editor/runs(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/results/content(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/results/preview(.:format)',
  '/api/:account_id/reports/:report_id/runs/:id/cancel(.:format)',
  '/api/:account_id/reports/:report_id/runs/:id/clone(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/options(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/duplicates(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/duplicates/:id(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/query_runs/:query_run_id/results(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/query_runs/:query_run_id/results/content(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/query_runs/:query_run_id/view/attrs(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/query_runs/:query_run_id/view/attrs/new(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/query_runs/:query_run_id/view/attrs/:id(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/query_runs/:query_run_id/view(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/query_runs/:id/cancel(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/query_runs(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/query_runs/:id(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/notebook_cell_runs(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/charts/:id/preview(.:format)',
  '/api/:account_id/reports/:report_id/exports/runs/:run_id/:format/new(.:format)',
  '/api/:account_id/reports/:report_id/exports/runs/:run_id/:format(.:format)',
  '/api/:account_id/reports/:report_id/exports/runs/:run_id/:format/download(.:format)',
  '/api/:account_id/reports/:report_id/exports/runs/:run_id/:format/regenerate(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/emails(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/emails/new(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/emails/:id(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/slack_messages(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/slack_messages/new(.:format)',
  '/api/:account_id/reports/:report_id/runs/:run_id/slack_messages/:id(.:format)',
  '/api/:account_id/reports/:report_id/runs(.:format)',
  '/api/:account_id/reports/:report_id/runs/:id(.:format)',
  '/api/:account_id/reports/:report_id/schedules/:schedule_id/runs(.:format)',
  '/api/:account_id/reports/:report_id/schedules/:schedule_id/subscriptions(.:format)',
  '/api/:account_id/reports/:report_id/schedules(.:format)',
  '/api/:account_id/reports/:report_id/schedules/:id(.:format)',
  '/api/:account_id/reports/schedules/v1/bulk_delete',
  '/api/:account_id/reports/:report_id/subscriptions/:subscription_id/email_memberships(.:format)',
  '/api/:account_id/reports/:report_id/subscriptions/:subscription_id/email_memberships/new(.:format)',
  '/api/:account_id/reports/:report_id/subscriptions/:subscription_id/email_memberships/:id(.:format)',
  '/api/:account_id/reports/:report_id/subscriptions/:subscription_id/slack_memberships(.:format)',
  '/api/:account_id/reports/:report_id/subscriptions/:subscription_id/slack_memberships/new(.:format)',
  '/api/:account_id/reports/:report_id/subscriptions/:subscription_id/slack_memberships/:id(.:format)',
  '/api/:account_id/reports/:report_id/subscriptions(.:format)',
  '/api/:account_id/reports/:report_id/subscriptions/:id(.:format)',
  '/api/:account_id/reports/:report_id/perspective_email_report_subscription_memberships(.:format)',
  '/api/:account_id/reports/:report_id/email_report_subscribers/validate(.:format)',
  '/api/:account_id/reports/:report_id/embed_keys(.:format)',
  '/api/:account_id/reports/:report_id/embed_keys/:id(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/sessions/:id/load_report_run(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/sessions/:id/cancel(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/sessions/:id/complete(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/sessions/:id/inspect(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/sessions(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/sessions/:id(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/batch_runs(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:id/export(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/cells/:cell_id/runs/:run_id/results/:id/result(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/cells/:cell_id/runs/:run_id/results(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/cells/:cell_id/runs/:run_id/results/:id(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/cells/:cell_id/runs(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/cells/:cell_id/runs/:id(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/cells(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:notebook_id/cells/:id(.:format)',
  '/api/:account_id/reports/:report_id/notebooks(.:format)',
  '/api/:account_id/reports/:report_id/notebooks/:id(.:format)',
  '/api/:account_id/reports/:report_id/layout_cells(.:format)',
  '/api/:account_id/reports/:report_id/layout_cells/:id(.:format)',
  '/api/:account_id/reports/:report_id/space_links(.:format)',
  '/api/:account_id/reports/:report_id/space_links/:id(.:format)',
  '/api/:account_id/reports/:report_id/switch_views/:switch_view_id/switch_attrs(.:format)',
  '/api/:account_id/reports/:report_id/switch_views/:switch_view_id/switch_attrs/new(.:format)',
  '/api/:account_id/reports/:report_id/switch_views/:switch_view_id/switch_attrs/:id(.:format)',
  '/api/:account_id/reports/:report_id/switch_views/:switch_view_id/:run_type(/:run_id)',
  '/api/:account_id/reports/:report_id/switch_views/:id(.:format)',
  '/api/:account_id/reports/:report_id/archive(.:format)',
  '/api/:account_id/reports/:report_id/unarchive(.:format)',
  '/api/:account_id/reports/:report_id/sync_to_github(.:format)',
  '/api/:account_id/reports/:report_id/update_settings(.:format)',
  '/api/:account_id/reports/:report_id/signed_embeds/enable(.:format)',
  '/api/:account_id/reports/:report_id/signed_embeds/disable(.:format)',
  '/api/:account_id/reports/:report_id/external_sharing/enable(.:format)',
  '/api/:account_id/reports/:report_id/external_sharing/disable(.:format)',
  '/api/:account_id/reports/:id/import_dataset(.:format)',
  '/api/:account_id/reports(.:format)',
  '/api/:account_id/reports/new(.:format)',
  '/api/:account_id/reports/:id(.:format)',
  '/api/:account_id/access_tokens/:id/verify(.:format)',
  '/api/:account_id/access_tokens(.:format)',
  '/api/:account_id/access_tokens/:id(.:format)',
  '/api/:account_id/api_keys/:id/revoke(.:format)',
  '/api/:account_id/api_keys/revoke_all(.:format)',
  '/api/:account_id/api_keys(.:format)',
  '/api/:account_id/api_keys/:id(.:format)',
  '/api/:account_id/bridges/setup(.:format)',
  '/api/:account_id/bridges(.:format)',
  '/api/:account_id/bridges/:id(.:format)',
  '/api/:account_id/report_views(.:format)',
  '/api/:account_id/report_views/:report_id(.:format)',
  '/api/:account_id/share_keys/:id(.:format)',
  '/api/:account_id/invites(.:format)',
  '/api/:account_id/invites/new(.:format)',
  '/api/:account_id/members/:token(.:format)',
  '/api/search_settings/:context_account_id(.:format)',
  '/api/signin_options/:email_or_username(.:format)',
  '/api/notebook_sessions/:id/status(.:format)',
  '/api/notebook_sessions/:notebook_session_id/loads/:id/status(.:format)',
  '/api/notebook_sessions/:id(.:format)',
  '/api/batch_runs/:id(.:format)',
  '/api/notebook_cell_runs/:notebook_cell_run_id/results(.:format)',
  '/api/account/validate(.:format)',
  '/api/account(.:format)',
  '/api/:account_id(.:format)',
  '/api/mailgun/events(.:format)',
  '/api/recurly/callback(.:format)',
  '/api/github_webhook_events/callback(.:format)',
];

type Scrambled = string | Array<Scrambled> | { [key: string]: Scrambled };

@Injectable()
export class ApiErrorLoggingInterceptor implements HttpInterceptor {
  private railsRoutes: { original: string; route: string; regex: RegExp; params: Key[] }[] = [];
  constructor(private bugsnag: BugsnagService) {
    this.railsRoutes = RAILS_ROUTES.map((route) => {
      const keys: Key[] = [];
      const original = route;
      route = route.replace(/\(\/(:\w+)\)/, '$1?'); // replace (/:param) with /:param? for better compatibility
      route = route.replace('(.:format)', ':format(.json)?'); // to support .json extension
      const regex = pathToRegexp(route, keys);
      return {
        original,
        route: route,
        regex: regex,
        params: keys,
      };
    });
  }

  /**
   * Scrambles an object or primitive according to the following specification:
   * - Numbers get a fixed placeholder with a precision and sign (i.e. -N4.3 would mean a
   *   negative number with 4 digits, a decimal point, and 3 more digits).
   * - booleans would just be "B"
   * - Strings would be scrambled with letters, numbers, and symbols all receiving a respective
   *   placeholder, for example, Test 123 # might be LLLLSNNSS.
   * - null, undefined would be "null" and "undefined"
   * - Infinity and NaN would be unique like (NaN) or (INF)
   * - Objects are recursively scrambled, however, the keys will be untou
   * - Arrays might include the length, and the first N (where N is a small number) elements
   *   using the above rules. i.e. A10[N4.3,N1.4,N6.3,...] or A3[{p1:LLLSN,p2:LLSNN},...]
   *
   * @example { "num": -1234.4, "string": "sensitive 1234" } -> { "num": "-N4.1", "string": "LLLLLLLLLSNNNN" }
   *
   * For more examples, see the specs
   *
   * @param payload an object or primitive to scramble
   * @returns scrambed object or primitive
   */
  scramblePayload(payload: string | number | boolean | bigint): string;
  scramblePayload(payload: any, recursing?: boolean): Scrambled;
  scramblePayload(payload: any, recursing = false): Scrambled {
    switch (typeof payload) {
      case 'string':
        try {
          payload = JSON.parse(payload);
          return this.scramblePayload(payload, true);
        } catch {
          // Do nothing
        }

        if (payload.length > 255) {
          return `<<<Length: ${payload.length}, ${Md5.hashStr(payload)}>>>`;
        } else {
          const output = Array<string>(payload.length);
          const lowercase = payload.toLowerCase();
          for (let ii = 0; ii < lowercase.length; ii++) {
            const code = lowercase.charCodeAt(ii);
            output[ii] = code >= 97 && code <= 122 ? 'L' : code >= 48 && code <= 57 ? 'N' : 'S';
          }

          return output.join('');
        }
      case 'number':
        if (isNaN(payload)) {
          return '<<<NaN>>>';
        }

        if (!isFinite(payload)) {
          return '<<<Infinity>>>';
        }

        const groups = payload.toString().replace('-', '').split(/\.|e/);
        let numberResult = (payload < 0 ? '-N' : 'N') + groups[0].length;

        if (groups.length === 2) {
          if (groups[1][0] === '+') {
            numberResult = numberResult + groups[1];
          } else {
            numberResult = numberResult + '.' + groups[1].length;
          }
        } else if (groups.length === 3) {
          numberResult = numberResult + '.' + groups[1].length + groups[2];
        }

        return numberResult;
      case 'bigint':
        return (payload < 0 ? '-N' : 'N') + payload.toString().replace('-', '').length;
      case 'boolean':
        return 'B';
      case 'function':
        return '<<<FUNCTION>>>';
      case 'object':
        if (Array.isArray(payload)) {
          return payload.map((v) => this.scramblePayload(v, true));
        }

        if (payload != null) {
          return Object.keys(payload).reduce((scrambled: any, next, index) => {
            let key = next;
            // If this isn't the first call, then scramble the keys
            if (recursing) {
              key = this.scramblePayload(key) + '_' + index;
            }

            scrambled[key] = this.scramblePayload(payload[next], true);
            return scrambled;
          }, {});
        } else {
          return JSON.stringify(payload);
        }
      case 'undefined':
        return 'undefined';
      default:
        return '<<<UNKNOWN>>>';
    }
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.url.match(/\/api\//i)) {
      return next.handle(request).pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse) {
            switch (err.status) {
              case 0:
                // Status 0 can be due to user aborting or extensions like ad-blockers
                // Most of these are noise, so don't log for now.
                break;
              case 412:
                // Ignore any Mode-reserved error codes that are not failures
                break;
              default: // check if includes protocol + hostname
                // Angular HttpHeaders object cannot be stringified, so converting to JSON
                const headers = request.headers.keys().reduce((acc, key) => {
                  acc[key] = request.headers.get(key);
                  return acc;
                }, {} as { [key: string]: string | string[] | null });

                const bugsnagInfo = {
                  rawUrl: request.url,
                  rawError: err.error,
                  status: err.status,
                  statusText: err.statusText,
                  method: request.method,
                  headers: this.scramblePayload(headers),
                } as any;

                // Using URL API would be simpler, would just need to not completely blow up on unsupported platforms
                const fullUrlMatch = /^[^#]*?:\/\/.*?(\/.*)$/.exec(request.url);
                let url = request.url;

                if (fullUrlMatch != null) {
                  url = fullUrlMatch[1];
                }

                const parts = url.split('?');

                const route = this.railsRoutes.find((r) => r.regex.test(parts[0]));
                const routeName = route?.original || 'UNKNOWN_ROUTE';

                if (route != null) {
                  // wasteful to do the same regex call,
                  // but this doesn't happen that often so easier to minimize logic
                  const match = route.regex.exec(parts[0]);
                  if (match && match.length > 1) {
                    bugsnagInfo.routeParams = route.params.reduce((acc, key, index) => {
                      acc[key.name] = match[index + 1];
                      return acc;
                    }, {} as any);
                  }
                }

                if (parts.length >= 2) {
                  bugsnagInfo.queryParams = parse('?' + parts[1]);
                }

                if (request.body != null) {
                  bugsnagInfo.body = this.scramblePayload(request.body);
                }

                let error = new Error(request.method.toUpperCase() + ' Call Failed: ' + routeName);
                if (request.context.get(INITIATOR_CONTEXT)) {
                  // try to keep the stack trace of the initiator of the HTTP call
                  const sourceError = request.context.get(INITIATOR_CONTEXT) as unknown as Error;
                  sourceError.message = error.message + '. Stack trace modified to show the initiator';
                  error = sourceError;
                }

                this.bugsnag.notify(error, (event: any) => {
                  event.errors[0].errorClass = `WebappApiError`;
                  event.context = request.method + ' ' + routeName;
                  event.groupingHash = request.method + '.' + routeName;
                  event.addMetadata('Call Info', bugsnagInfo);
                });
                break;
            }
          }

          return throwError(err);
        })
      );
    } else {
      return next.handle(request);
    }
  }
}
